import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FilterActionName, FilterStateAction, useCreateFilterStateBranch, useFilterAction, useFilterDispatch, useFilterState } from "../../state/FilterContext";
import { FilterState } from "../../state/state-models";
import { FilterStateControllerContext, FilterStateControllerTarget, FilterStateControllerTiming, FilterStateControllerRequestOptions } from "../../state/filter-state-controller.context";

interface FilterStateControllerProps {
    timing: FilterStateControllerTiming;
    target: FilterStateControllerTarget;
    children: React.ReactNode;
}

function FilterStateControllerScope({ timing, target, children }: FilterStateControllerProps): JSX.Element {
    const location = useLocation();
    const currentState = useFilterState();
    const setScopedState = useFilterAction(FilterActionName.SET_STATE);
    const { dispatch } = useFilterDispatch();
    const [pendingState, setPendingState] = useState<FilterState | null>(null);
    const previousPathRef = useRef(location.pathname + location.search + location.hash);

    const commitState = useCallback((nextState: FilterState) => {
        if (target === "global") {
            dispatch({ type: FilterActionName.SET_STATE, payload: nextState } as FilterStateAction);
            return;
        }
        setScopedState(nextState);
    }, [dispatch, setScopedState, target]);

    const hasPendingChanges = pendingState !== null;
    const effectiveState = useMemo(() => pendingState || currentState, [pendingState, currentState]);

    const requestChange = useCallback((recipe: (state: FilterState) => FilterState, _options: FilterStateControllerRequestOptions) => {
        if (timing === "immediate") {
            commitState(recipe(currentState));
            return;
        }

        setPendingState((previousPending) => {
            const base = previousPending || currentState;
            return recipe(base);
        });
    }, [commitState, currentState, timing]);

    const apply = useCallback(() => {
        if (!pendingState) {
            return;
        }
        commitState(pendingState);
        setPendingState(null);
    }, [commitState, pendingState]);

    const clearPending = useCallback(() => {
        setPendingState(null);
    }, []);

    useEffect(() => {
        const currentPath = location.pathname + location.search + location.hash;
        if (currentPath !== previousPathRef.current) {
            previousPathRef.current = currentPath;
            setPendingState(null);
        }
    }, [location.hash, location.pathname, location.search]);

    const contextValue = useMemo(() => ({
        timing,
        target,
        hasPendingChanges,
        pendingState,
        effectiveState,
        requestChange,
        apply,
        clearPending,
    }), [timing, target, hasPendingChanges, pendingState, effectiveState, requestChange, apply, clearPending]);

    return (
        <FilterStateControllerContext.Provider value={contextValue}>
            {children}
        </FilterStateControllerContext.Provider>
    );
}

export function FilterStateController({ timing, target, children }: FilterStateControllerProps): JSX.Element {
    const createBranch = useCreateFilterStateBranch();
    const BranchComponent = useMemo(() => {
        if (target !== "branch") {
            return null;
        }
        return createBranch({});
    }, [createBranch, target]);

    if (BranchComponent) {
        return (
            <BranchComponent>
                <FilterStateControllerScope timing={timing} target={target}>
                    {children}
                </FilterStateControllerScope>
            </BranchComponent>
        );
    }

    return (
        <FilterStateControllerScope timing={timing} target={target}>
            {children}
        </FilterStateControllerScope>
    );
}
